import {
  Keypair,
  SystemProgram,
  Transaction,
  PublicKey,
} from "@solana/web3.js";
import {
  MINT_SIZE,
  TOKEN_PROGRAM_ID,
  createInitializeMint2Instruction,
  createAssociatedTokenAccountInstruction,
  createMintToInstruction,
  getAssociatedTokenAddressSync,
  getMinimumBalanceForRentExemptMint,
} from "@solana/spl-token";

/**
 * Create an SPL Token mint using the browser wallet as the fee payer + mint authority.
 * NEVER handles private keys — signing happens inside the wallet extension.
 *
 * @param {import("@solana/web3.js").Connection} connection
 * @param {{ publicKey: PublicKey, signTransaction: (tx: Transaction) => Promise<Transaction> }} wallet
 * @param {{ decimals: number, supply: string|number|bigint }} params
 * @param {(stage: string) => void} onStage
 * @returns {Promise<{ mintAddress: string, ata: string, signature: string, supplyRaw: string }>}
 */
/**
 * Create an SPL Token mint using the browser wallet as the fee payer + mint authority.
 * NEVER handles private keys — signing happens inside the wallet extension.
 *
 * @param {import("@solana/web3.js").Connection | (() => import("@solana/web3.js").Connection)} connectionOrGetter
 *        Either a Connection instance or a zero-arg function returning the CURRENT Connection
 *        (preferred — allows mid-flight RPC URL changes to be picked up on the next RPC call).
 * @param {{ publicKey: PublicKey, signTransaction: (tx: Transaction) => Promise<Transaction> }} wallet
 * @param {{ decimals: number, supply: string|number|bigint }} params
 * @param {(stage: string) => void} onStage
 * @returns {Promise<{ mintAddress: string, ata: string, signature: string, supplyRaw: string }>}
 */
export async function createSplToken(connectionOrGetter, wallet, params, onStage = () => {}) {
  const getConnection = typeof connectionOrGetter === "function"
    ? connectionOrGetter
    : () => connectionOrGetter;

  if (!wallet?.publicKey) throw new Error("Wallet not connected");
  if (!wallet?.signTransaction) throw new Error("Wallet does not support signing");
  const decimals = Number(params.decimals);
  if (!Number.isInteger(decimals) || decimals < 0 || decimals > 9) {
    throw new Error("Decimals must be an integer between 0 and 9");
  }
  const uiSupply = String(params.supply || "").trim();
  if (!/^\d+$/.test(uiSupply) || uiSupply === "0") {
    throw new Error("Total supply must be a positive integer");
  }
  const supplyRaw = BigInt(uiSupply) * (10n ** BigInt(decimals));

  const log = (...args) => console.log("[STICKY:createSplToken]", ...args);
  const rpcOf = (c) => c?.rpcEndpoint || "(unknown endpoint)";

  onStage("Preparing transaction…");
  const mint = Keypair.generate();
  const payer = wallet.publicKey;
  const ata = getAssociatedTokenAddressSync(mint.publicKey, payer);

  let conn = getConnection();
  log("RPC endpoint at start:", rpcOf(conn));
  const rent = await getMinimumBalanceForRentExemptMint(conn);
  log("mint keypair:", mint.publicKey.toBase58());
  log("payer:", payer.toBase58(), "ATA:", ata.toBase58(), "rent:", rent);

  conn = getConnection();
  log("RPC before getLatestBlockhash:", rpcOf(conn));
  const { blockhash, lastValidBlockHeight } = await conn.getLatestBlockhash("confirmed");
  log("blockhash:", blockhash, "lastValidBlockHeight:", lastValidBlockHeight);

  const tx = new Transaction({ feePayer: payer, blockhash, lastValidBlockHeight }).add(
    SystemProgram.createAccount({
      fromPubkey: payer,
      newAccountPubkey: mint.publicKey,
      space: MINT_SIZE,
      lamports: rent,
      programId: TOKEN_PROGRAM_ID,
    }),
    createInitializeMint2Instruction(mint.publicKey, decimals, payer, payer),
    createAssociatedTokenAccountInstruction(payer, ata, payer, mint.publicKey),
    createMintToInstruction(mint.publicKey, ata, payer, supplyRaw),
  );

  // Mint keypair must sign for its own creation; wallet signs the rest.
  tx.partialSign(mint);

  onStage("Waiting for wallet approval…");
  const signed = await wallet.signTransaction(tx);
  log("wallet signed the transaction");

  onStage("Sending transaction…");
  const rawTx = signed.serialize();
  conn = getConnection();
  log("RPC before sendRawTransaction:", rpcOf(conn));
  let signature;
  try {
    signature = await conn.sendRawTransaction(rawTx, {
      skipPreflight: false,
      preflightCommitment: "confirmed",
      maxRetries: 5,
    });
    log("sendRawTransaction returned signature:", signature);
  } catch (e) {
    log("sendRawTransaction FAILED:", e);
    throw new Error("sendRawTransaction failed: " + (e?.message || e));
  }

  // ------------------------------------------------------------------
  // HTTP-only confirmation (no WebSocket subscription).
  // Reason: ngrok free tier + Solana test-validator do not reliably
  // tunnel `wss://` for signatureSubscribe, causing confirmTransaction
  // to hang until the blockhash expires. Polling getSignatureStatuses
  // over plain HTTPS avoids that entirely and works on Devnet too.
  // ------------------------------------------------------------------
  onStage("Confirming transaction…");
  const pollStartedAt = Date.now();
  const timeoutMs = 90_000;
  const pollIntervalMs = 1200;
  let confirmed = false;
  let onchainErr = null;
  let lastConfirmationStatus = null;
  let currentHeight = null;

  while (Date.now() - pollStartedAt < timeoutMs) {
    // 1) Check signature status (uses the LATEST Connection, so if the user
    //    changed the ngrok URL mid-flight we pick up the new endpoint).
    try {
      conn = getConnection();
      log("RPC before getSignatureStatuses:", rpcOf(conn));
      const res = await conn.getSignatureStatuses([signature], { searchTransactionHistory: false });
      const status = res?.value?.[0];
      if (status) {
        lastConfirmationStatus = status.confirmationStatus || "processed";
        if (status.err) {
          onchainErr = status.err;
          log("signature err:", status.err);
          break;
        }
        if (status.confirmationStatus === "confirmed" || status.confirmationStatus === "finalized") {
          confirmed = true;
          log("confirmed at status:", status.confirmationStatus, "slot:", status.slot);
          break;
        }
      }
    } catch (e) {
      log("getSignatureStatuses error (will retry):", e?.message || e);
    }

    // 2) Check if block height has passed lastValidBlockHeight
    try {
      conn = getConnection();
      log("RPC before getBlockHeight:", rpcOf(conn));
      currentHeight = await conn.getBlockHeight("confirmed");
      if (currentHeight > lastValidBlockHeight) {
        log("block height exceeded — height:", currentHeight, "expiry:", lastValidBlockHeight);
        break;
      }
    } catch (e) {
      log("getBlockHeight error (will retry):", e?.message || e);
    }

    await new Promise((r) => setTimeout(r, pollIntervalMs));
  }

  if (onchainErr) {
    throw new Error("Transaction failed on-chain: " + JSON.stringify(onchainErr));
  }
  if (!confirmed) {
    const elapsed = Math.round((Date.now() - pollStartedAt) / 1000);
    log("NOT confirmed after", elapsed, "s. lastStatus:", lastConfirmationStatus, "height:", currentHeight, "expiry:", lastValidBlockHeight);
    // Final check with searchTransactionHistory in case the RPC just hasn't caught up
    try {
      conn = getConnection();
      log("RPC before final getSignatureStatuses:", rpcOf(conn));
      const finalRes = await conn.getSignatureStatuses([signature], { searchTransactionHistory: true });
      const finalStatus = finalRes?.value?.[0];
      log("final status check:", finalStatus);
      if (finalStatus && !finalStatus.err &&
          (finalStatus.confirmationStatus === "confirmed" || finalStatus.confirmationStatus === "finalized")) {
        confirmed = true;
      } else if (finalStatus?.err) {
        throw new Error("Transaction failed on-chain: " + JSON.stringify(finalStatus.err));
      }
    } catch (e) {
      log("final status check error:", e?.message || e);
    }
    if (!confirmed) {
      throw new Error(
        `Transaction not confirmed after ${elapsed}s (last status: ${lastConfirmationStatus || "unknown"}). ` +
        `Signature: ${signature}. Check your Localnet RPC connection and try again.`
      );
    }
  }

  onStage("Token created successfully.");
  log("Success. mint:", mint.publicKey.toBase58(), "sig:", signature);
  return {
    mintAddress: mint.publicKey.toBase58(),
    ata: ata.toBase58(),
    signature,
    supplyRaw: supplyRaw.toString(),
  };
}

/**
 * Placeholder metadata service. Not implemented in Phase 3 — kept modular so
 * a Metaplex writer can be added later without touching createSplToken.
 */
export const metadataService = {
  isAvailable() { return false; },
  async attachMetadata(/* params */) {
    return { ok: false, reason: "Metadata upload not implemented yet." };
  },
};

/**
 * TEST-MODE payment service backed by the FastAPI Launchpad payments API.
 * NO real funds are transferred. All records are labeled `LOCALNET_TEST` /
 * `DEVNET_TEST` — real Mainnet payments are intentionally rejected server-side.
 *
 * The mint keypair signs its own account creation; the wallet still signs
 * all other instructions — private keys never leave the wallet extension.
 */
const PAYMENT_API = (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL)
  ? `${process.env.REACT_APP_BACKEND_URL}/api/launchpad/payments`
  : "/api/launchpad/payments";

export const paymentService = {
  mode: "TEST",              // legacy label kept for existing callers
  async getPaymentAllocation() {
    try {
      const r = await fetch(`${PAYMENT_API}/config`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return await r.json();
    } catch (e) {
      // Local fallback — matches backend defaults; UI still renders on failure.
      return {
        feeUsd: 99, currency: "USD",
        split: { ecosystem: 40, buyBurn: 40, operations: 20 },
        allocations: { ecosystemAllocation: 39.6, buyBurnAllocation: 39.6, operationsAllocation: 19.8 },
        mode: "LOCALNET_TEST", mainnetLive: false,
      };
    }
  },
  async createTestPayment({ wallet, mode, idempotencyKey }) {
    if (!wallet) throw new Error("Wallet is required for a test payment.");
    const body = { wallet, mode: (mode || "LOCALNET_TEST").toUpperCase(), idempotencyKey };
    const r = await fetch(`${PAYMENT_API}/test`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const text = await r.text().catch(() => "");
      throw new Error(`Test payment failed (${r.status}): ${text || "unknown"}`);
    }
    return await r.json();
  },
  async getPaymentStatus(paymentId) {
    if (!paymentId) return null;
    const r = await fetch(`${PAYMENT_API}/${paymentId}`);
    if (!r.ok) return null;
    return await r.json();
  },
  async attachMint(paymentId, mintAddress) {
    if (!paymentId || !mintAddress) return null;
    try {
      const r = await fetch(`${PAYMENT_API}/${paymentId}/mint`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mintAddress }),
      });
      if (!r.ok) return null;
      return await r.json();
    } catch { return null; }
  },
  /**
   * Phase 10 — creates a LIVE (Mainnet) payment intent on the server.
   * The backend fails-closed with HTTP 503 while LIVE_PAYMENTS_ENABLED is off,
   * so this call is safe to make unconditionally. The full on-chain transfer
   * + verification lives in launchFeePaymentService.runLivePayment().
   */
  async createLivePayment({ wallet, currency, idempotencyKey }) {
    if (!wallet) throw new Error("Wallet is required.");
    const body = { wallet, currency: (currency || "SOL").toUpperCase(), idempotencyKey };
    const r = await fetch(`${PAYMENT_API}/live`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const text = await r.text().catch(() => "");
    if (!r.ok) throw new Error(`LIVE payment intent failed (HTTP ${r.status}): ${text || "disabled"}`);
    try { return JSON.parse(text); } catch { return null; }
  },
  async isLiveEnabled() {
    try {
      const cfg = await this.getPaymentAllocation();
      return Boolean(cfg?.liveEnabled);
    } catch { return false; }
  },
  // Kept for backwards compatibility with earlier callers.
  async chargeLaunchFee() {
    return { ok: true, mode: "TEST", chargedUsd: 0 };
  },
};

/**
 * Placeholder Buy & Burn service. Disabled until STICKY token launches.
 */
export const buyAndBurnService = {
  enabled: false,
  async buyAndBurn() {
    return { ok: false, reason: "STICKY has not launched. Buy & Burn is disabled." };
  },
};
